/**
 * Capture logic for the product-tour author overlay. Pure DOM → data: given a
 * clicked element, produce a loose, best-effort description that a non-engineer
 * author can hand to the tour-building agent. Works in production (reads only
 * the live DOM — no React source/fiber metadata, which prod strips).
 *
 * When the element already carries a stable anchor we return it directly as the
 * `selector`. When it doesn't (`needsAnchor: true`) we capture enough
 * surrounding context (label, role, nearest anchored ancestor, breadcrumb,
 * nearby headings, route) for the building agent to locate the element and add
 * a `data-tour-id` in dev.
 */

/** Attributes we treat as stable anchors, in preference order. */
const ANCHOR_ATTRS = [
  'data-test-id',
  'data-click-id',
  'data-tour-id',
  'id',
] as const;

/** Selectors that mark an element as the "meaningful" thing the user meant. */
const INTERACTIVE_SELECTOR =
  'button, a, input, select, textarea, label, [role="button"], [role="link"], [role="tab"], [role="menuitem"], [role="checkbox"]';

const MAX_BREADCRUMB = 6;
const MAX_FALLBACK_DEPTH = 5;
const LABEL_MAX = 80;

export type CapturedAnchor = {
  /** The element's own stable-anchor selector, or null when it has none. */
  selector: string | null;
  /** True when no stable anchor exists → the building agent must add one. */
  needsAnchor: boolean;
  /** Visible text / aria-label of the element. */
  label: string;
  role: string | null;
  tag: string;
  /** Route the element was captured on (pathname). */
  surface: string;
  /** Nearest ancestor that has a stable anchor — context for locating the element. */
  nearestAnchoredAncestor: string | null;
  /** Root → element trail of short node descriptions. */
  ancestorBreadcrumb: string[];
  /** Nearby section/heading text, for disambiguation. */
  nearbyText: string[];
  /** Brittle structural selector — reference only, never the final anchor. */
  cssFallback: string;
  /** Free-text the author can fill in afterwards. */
  authorNote: string;
};

const collapse = (text: string | null | undefined, max: number): string =>
  (text ?? '').replace(/\s+/g, ' ').trim().slice(0, max);

const ownAnchorSelector = (el: Element): string | null => {
  const attr = ANCHOR_ATTRS.find(anchorAttr => el.getAttribute(anchorAttr));
  return attr ? `[${attr}="${el.getAttribute(attr)}"]` : null;
};

const hasAnchor = (el: Element): boolean =>
  ANCHOR_ATTRS.some(attr => el.hasAttribute(attr));

/** Element → ancestors trail (inclusive), climbing up to `max` nodes, stopping before BODY. */
const ancestorChain = (node: HTMLElement | null, max: number): HTMLElement[] =>
  node === null || node.tagName === 'BODY' || max <= 0
    ? []
    : [node, ...ancestorChain(node.parentElement, max - 1)];

/**
 * Resolve a clicked node to the element the author most likely meant: climb a
 * few levels to the nearest anchored or interactive ancestor (so clicking an
 * icon inside a button captures the button).
 */
const resolveTarget = (node: HTMLElement, level = 5): HTMLElement | null => {
  if (hasAnchor(node) || node.matches(INTERACTIVE_SELECTOR)) return node;
  return level > 1 && node.parentElement
    ? resolveTarget(node.parentElement, level - 1)
    : null;
};

const labelOf = (el: HTMLElement): string =>
  collapse(
    el.getAttribute('aria-label') ||
      el.textContent ||
      el.getAttribute('title') ||
      el.getAttribute('placeholder'),
    LABEL_MAX
  );

const nearestAnchoredAncestor = (el: HTMLElement): string | null => {
  const parent = el.parentElement;
  if (!parent || parent.tagName === 'BODY') return null;
  return ownAnchorSelector(parent) ?? nearestAnchoredAncestor(parent);
};

const describeNode = (el: HTMLElement): string => {
  const tag = el.tagName.toLowerCase();
  const anchorSelector = ownAnchorSelector(el);
  if (anchorSelector) return `${tag}${anchorSelector}`;
  const role = el.getAttribute('role');
  const text = collapse(el.getAttribute('aria-label') || el.textContent, 30);
  if (role)
    return text ? `${tag}[role=${role}] '${text}'` : `${tag}[role=${role}]`;
  return text ? `${tag} '${text}'` : tag;
};

const ancestorBreadcrumb = (el: HTMLElement): string[] =>
  ancestorChain(el, MAX_BREADCRUMB).map(describeNode).reverse();

const headingTextOf = (el: HTMLElement): string =>
  collapse(
    el.querySelector('h1, h2, h3, h4, [role="heading"]')?.textContent,
    40
  );

const firstOccurrence = (
  value: string,
  index: number,
  all: string[]
): boolean => all.indexOf(value) === index;

const nearbyText = (el: HTMLElement): string[] =>
  ancestorChain(el, 8)
    .map(headingTextOf)
    .filter(text => text !== '')
    .filter(firstOccurrence)
    .slice(0, 3);

const nodeSelector = (node: HTMLElement): string => {
  const tag = node.tagName.toLowerCase();
  const parent = node.parentElement;
  if (!parent) return tag;
  const sameTag = Array.from(parent.children).filter(
    child => child.tagName === node.tagName
  );
  if (sameTag.length <= 1) return tag;
  return `${tag}:nth-of-type(${sameTag.indexOf(node) + 1})`;
};

const cssFallbackParts = (
  node: HTMLElement | null,
  depth: number
): string[] => {
  if (!node || node.tagName === 'BODY' || depth <= 0) return [];
  if (node.id) return [`#${CSS.escape(node.id)}`];
  return [
    ...cssFallbackParts(node.parentElement, depth - 1),
    nodeSelector(node),
  ];
};

const cssFallback = (el: HTMLElement): string =>
  cssFallbackParts(el, MAX_FALLBACK_DEPTH).join(' > ');

export const describeAnchor = (clicked: HTMLElement): CapturedAnchor => {
  const target = resolveTarget(clicked) ?? clicked;
  const selector = ownAnchorSelector(target);
  return {
    selector,
    needsAnchor: selector === null,
    label: labelOf(target),
    role: target.getAttribute('role'),
    tag: target.tagName.toLowerCase(),
    surface: window.location.pathname,
    nearestAnchoredAncestor: nearestAnchoredAncestor(target),
    ancestorBreadcrumb: ancestorBreadcrumb(target),
    nearbyText: nearbyText(target),
    cssFallback: cssFallback(target),
    authorNote: '',
  };
};
